import React, { useState, useEffect } from 'react';
import './App.css';
import logo from './logo.svg';
import TourForm from './components/TourForm';
import TourLegForm from './components/TourLegForm';
import ObservationForm from './components/ObservationForm';
import ObservationList from './components/ObservationList';
import QuoteWindow from './components/QuoteWindow';
import PriceHistory from './components/PriceHistory';
import HotelBooking from './components/HotelBooking';
import RailPrices from './components/RailPrices';
import Login from './components/Login';
import { authApi } from './api/client';

// Every tab, in order. Only staff sees them all; the booking role, the account
// ETG certifies with (task 1.25), only sees the booking screen. The backend
// refuses the rest to that role as well, so this is about a clean screen, not
// about security.
const TABS = [
  { key: 'tour', label: 'Manage Tours' },
  { key: 'legs', label: 'Manage Tour Legs' },
  { key: 'new-observation', label: 'Waarneming toevoegen' },
  { key: 'observations', label: 'Waarnemingen' },
  { key: 'quotes', label: 'Offertes' },
  { key: 'price-history', label: 'Prijsverloop' },
  { key: 'hotel-booking', label: 'Hotel boeken', anyRole: true },
  { key: 'rail-prices', label: 'Treinprijzen' },
];

function App() {
  const [activeTab, setActiveTab] = useState('quotes');
  // Goes up whenever an observation is added, so the list refreshes without
  // the two components having to know each other.
  const [observationCount, setObservationCount] = useState(0);
  const [authed, setAuthed] = useState(!!localStorage.getItem('token'));
  const [user, setUser] = useState(null);
  const [userFailed, setUserFailed] = useState(false);

  useEffect(() => {
    if (authed) {
      setUserFailed(false);
      authApi
        .me()
        .then((res) => setUser(res.data))
        .catch(() => setUserFailed(true));
    }
  }, [authed]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setAuthed(false);
  };

  if (!authed) {
    return <Login onLogin={() => setAuthed(true)} />;
  }

  // Nothing is shown before we know the role, so the booking role never
  // briefly opens a tab it may not see.
  const tabs = user ? TABS.filter((t) => t.anyRole || user.role === 'staff') : [];
  const tab = tabs.some((t) => t.key === activeTab) ? activeTab : tabs.length ? tabs[0].key : null;

  return (
    <div className="App">
      <header className="App-header">
        <img src={logo} alt="Ergens Onderweg" className="logo" />
        <div className="title-block">
          <h1>look2book</h1>
          <p>Train Tour Pricing Management</p>
        </div>
        <div className="header-user">
          {user && <span>{user.full_name}</span>}
          <button className="btn-logout" onClick={handleLogout}>
            Uitloggen
          </button>
        </div>
      </header>

      {user && (
        <nav className="nav-tabs">
          {tabs.map((t) => (
            <button key={t.key} className={tab === t.key ? 'active' : ''} onClick={() => setActiveTab(t.key)}>
              {t.label}
            </button>
          ))}
        </nav>
      )}

      <main className="container">
        {!user && !userFailed && <p className="loading">Laden</p>}
        {userFailed && (
          <div className="message error">Je gegevens konden niet geladen worden. Herlaad de pagina.</div>
        )}
        {tab === 'tour' && <TourForm />}
        {tab === 'legs' && <TourLegForm />}
        {tab === 'new-observation' && (
          <ObservationForm onSaved={() => setObservationCount((n) => n + 1)} />
        )}
        {tab === 'observations' && (
          <ObservationList refreshKey={observationCount} />
        )}
        {tab === 'quotes' && <QuoteWindow />}
        {tab === 'price-history' && <PriceHistory />}
        {tab === 'hotel-booking' && <HotelBooking />}
        {tab === 'rail-prices' && <RailPrices />}
      </main>
    </div>
  );
}

export default App;
